import React, { FC, useState, useRef, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TextField, Panel, Form, Language, requiredItem, VirtualizedComboBox } from '@eta/components';
import { DefaultButton, PrimaryButton, IDropdownOption } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import {
    GetTaxpayerAddressDto,
    GetTaxpayerBranchDto,
} from '../../Services/ClientProxy/clientProxy';
import { ReduxState } from '../../common/reducers';
import { loadReferenceData } from '../../ReferenceData/Redux/Action';

import './EditAddress.scss';

export interface EditAddressProps {
    onClose: () => void;
    saveAddress: (address?: GetTaxpayerAddressDto, branchName?: string) => void;
    branch?: GetTaxpayerBranchDto;
}

const mapStateToProps = (state: ReduxState) => ({
    currentLanguage: state.reduxlanguage.language,
    lastError: state.profile.lastError,
    referenceData: state.referenceData?.taxpayersConfig
        ? {
            optionsCity:
                state.referenceData?.taxpayersConfig?.cities?.map<
                    IDropdownOption
                >((v) => {
                    return { key: v.id || 0,
                         text: v.name || '' ,
                         data: v.parentCode || ''
                        };
                }) ?? [],
            optionsGovernorate:
                state.referenceData?.taxpayersConfig?.governorates?.map<
                    IDropdownOption
                >((v) => {
                    return {
                        key: v.id || 0,
                        text: `${v.name}` || '',
                        data: v.code || ''
                    };
                }) ?? [],
            optionsCountry:
                state.referenceData?.taxpayersConfig?.countries?.map<
                    IDropdownOption
                >((v) => {
                    return {
                        key: v.id || 0,
                        text: `${v.name}` || '',
                        data: `${v.code}`
                    };
                }) ?? [],
        }
        : null,
});
const mapDispatchToProps = {
    dispatchloadReferenceData: loadReferenceData,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export const EditAddress: FC<PropsFromRedux & EditAddressProps> = (
    props: PropsFromRedux & EditAddressProps
) => {
    const { t } = useTranslation(['resources', 'common']);
    const {
        dispatchloadReferenceData,
        referenceData,
        currentLanguage: propsCurrentLanguage,
        branch,
        saveAddress,
        onClose
    } = props;
    const [currentLanguage, setCurrentLanguage] = useState<Language>();
    const [branchName, setBranchName] = useState(branch?.nameSecondaryLang || branch?.sourceAddress);
    const [currentAddress, setCurrentAddress] = useState(branch?.address ?? new GetTaxpayerAddressDto());
    const [CityFilter, setCityFilter] = useState<IDropdownOption[] | undefined>();
    const form = useRef(new Form({}));
    const [isFormValid, setIsFormValid] = useState<boolean>(
        form.current.isValid
    );

    form.current.onValidityChanged = (isValid) => setIsFormValid(isValid);

    const form2 = useRef(new Form({}));
    const [isFormValid2, setIsFormValid2] = useState<boolean>(
        form2.current.isValid
    );

    form2.current.onValidityChanged = (isValid) => setIsFormValid2(isValid);

    useEffect(() => {
        if (
            referenceData === null ||
            propsCurrentLanguage !== currentLanguage
        ) {
            setCurrentLanguage(propsCurrentLanguage);
            dispatchloadReferenceData(propsCurrentLanguage);
        }
    }, [
        referenceData,
        dispatchloadReferenceData,
        propsCurrentLanguage,
        currentLanguage,
    ]);

    useEffect(() => {
        
        const GovernorateCode = referenceData?.optionsGovernorate.find((x) => x?.key === currentAddress?.governorateID);
        const filterCity = referenceData?.optionsCity.filter((item) => item?.data === GovernorateCode?.data);
        setCityFilter(filterCity);

    },[currentAddress,referenceData]);

    const saveForm = async () => {
    
        if(currentAddress?.countryCode === 'EG' && form.current.isValid ) {
            saveAddress(currentAddress, branchName);
        } else if (currentAddress?.countryCode !== 'EG' && form2.current.isValid) {
                saveAddress(currentAddress, branchName);
           
        }
    };

    return (
        <Panel
            title={t('form.TaxPayer.AddressDetails')}
            allowEdition={false}
            editable
            editTitle={t('common:edit')}
            footer={(
                <>
                    <DefaultButton
                        text={t('common:cancel')}
                        onClick={() => onClose()}
                    />
                    <PrimaryButton
                        text={t('common:save')}
                        onClick={() => saveForm()}
                        disabled={currentAddress?.countryCode === 'EG' ? !isFormValid : !isFormValid2}
                    />
                </>
            )}
        >
            <div className="bodyLine branchesInfoEdit">
                <TextField
                    label={t('form.TaxPayer.BranchName')}
                    value={branchName}
                    onChange={(_, v) => setBranchName(v ?? '')}
                    required
                    validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.BranchName'))}
                    onValidationChange={(isValid) => {
                        form.current.SetValidity('branchName', isValid);
                        form2.current.SetValidity('branchName', isValid);
                        }
                    }
                />
                <VirtualizedComboBox
                    selectedKey={currentAddress?.countryID}
                    options={referenceData?.optionsCountry ?? []}
                    label={t('form.TaxPayer.Country')}
                    onChange={(_, v) =>
                        setCurrentAddress(
                            new GetTaxpayerAddressDto({
                                ...currentAddress,
                                countryID: Number(v?.key) ?? '',
                                // countryCode: v?.text.split('-')[0],
                                countryCode: v?.data,
                                cityID: 0,
                                governorateID: 0,
                                foreignCityName: '',
                                foreignGovernorateName: '',
                            })
                        )
                    }
                    required
                    validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.Country'))}
                    onValidationChange={(isValid) => {
                        form.current.SetValidity('countryID', isValid);
                        form2.current.SetValidity('countryID', isValid);
                        }
                    }
                    multiSelectDelimiter=''
                    allowFreeform
                    useComboBoxAsMenuWidth
                />
                {currentAddress?.countryCode === 'EG' ? (
                    <VirtualizedComboBox
                        selectedKey={currentAddress?.governorateID}
                        options={referenceData?.optionsGovernorate ?? []}
                        label={t('form.TaxPayer.Governorate')}
                        onChange={(_, v) =>{
                            setCurrentAddress(
                                new GetTaxpayerAddressDto({
                                    ...currentAddress,
                                    governorateID: Number(v?.key) ?? '',
                                })
                            );
                            const filterCity = referenceData?.optionsCity.filter((item) =>item.data === v?.data);
                            setCityFilter(filterCity);
                        }
                        }
                        required
                        validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.Governorate'))}
                        onValidationChange={(isValid) => {
                            form.current.SetValidity('governorateID', isValid);
                            
                            }
                        }
                        multiSelectDelimiter=''
                        allowFreeform
                        useComboBoxAsMenuWidth
                    />
                    
                ) : (
                        <TextField
                            label={t('form.TaxPayer.Governorate')}
                            value={currentAddress?.foreignGovernorateName}
                            onChange={(_, v) =>
                                setCurrentAddress(
                                    new GetTaxpayerAddressDto({
                                        ...currentAddress,
                                        foreignGovernorateName: v ?? '',
                                    })
                                )
                            }
                            required
                            validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.Governorate'))}
                            onValidationChange={(isValid) => {
                                form2.current.SetValidity('foreignGovernorateName', isValid);
                                }
                            }
                        />
                    )}
                {currentAddress?.countryCode === 'EG' ? (
                    <VirtualizedComboBox
                        selectedKey={currentAddress?.cityID}
                        options={CityFilter ?? []}
                        label={t('form.TaxPayer.City')}
                        onChange={(_, v) =>
                            setCurrentAddress(
                                new GetTaxpayerAddressDto({
                                    ...currentAddress,
                                    cityID: Number(v?.key) ?? '',
                                })
                            )
                        }
                        required
                        validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.City'))}
                        onValidationChange={(isValid) => {
                            form.current.SetValidity('cityID', isValid);
                            }
                        }
                        multiSelectDelimiter=''
                        allowFreeform
                        useComboBoxAsMenuWidth
                        
                    />
                ) : (
                        <TextField
                            label={t('form.TaxPayer.City')}
                            value={currentAddress?.foreignCityName}
                            onChange={(_, v) =>
                                setCurrentAddress(
                                    new GetTaxpayerAddressDto({
                                        ...currentAddress,
                                        foreignCityName: v ?? '',
                                    })
                                )
                            }
                            required
                        validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.City'))}

                            onValidationChange={(isValid) => {
                                form2.current.SetValidity('foreignCityName', isValid);
                                }
                            }
                        />
                    )}
            </div>
            <div className="bodyLine">
                <TextField
                    label={t('form.TaxPayer.BuildingName')}
                    value={currentAddress?.buildingNo}
                    onChange={(_, v) =>
                        setCurrentAddress(
                            new GetTaxpayerAddressDto({
                                ...currentAddress,
                                buildingNo: v ?? '',
                            })
                        )
                    }
                    required
                    validationFunc={(v) => requiredItem(t, v, t('form.TaxPayer.BuildingName'))}
                    onValidationChange={(isValid) => {
                        form.current.SetValidity('buildingNo', isValid);
                        form2.current.SetValidity('buildingNo', isValid);
                        }
                    }
                />
                <div className="mb-0 mx-0 flatFloor">
                    <TextField
                        label={t('form.TaxPayer.FlatNo')}
                        value={currentAddress?.flatNo}
                        onChange={(_, v) =>
                            setCurrentAddress(
                                new GetTaxpayerAddressDto({
                                    ...currentAddress,
                                    flatNo: v ?? '',
                                })
                            )
                        }
                    />
                    <TextField
                        label={t('form.TaxPayer.FloorNo')}
                        value={currentAddress?.floorNo}
                        onChange={(_, v) =>
                            setCurrentAddress(
                                new GetTaxpayerAddressDto({
                                    ...currentAddress,
                                    floorNo: v ?? '',
                                })
                            )
                        }
                    />
                </div>
            </div>
            <div className="bodyLine merge2">
                <TextField
                    label={t('form-components.StreetName')}
                    value={currentAddress?.streetName}
                    onChange={(_, v) =>
                        setCurrentAddress(
                            new GetTaxpayerAddressDto({
                                ...currentAddress,
                                streetName: v ?? '',
                            })
                        )
                    }
                    required
                    validationFunc={(v) => requiredItem(t, v, t('form-components.StreetName'))}
                    onValidationChange={(isValid) => {
                        form.current.SetValidity('streetName', isValid);
                        form2.current.SetValidity('streetName', isValid);
                        }
                    }
                />
            </div>
            <div className="bodyLine merge2">
                <TextField
                    label={t('form.TaxPayer.LandMark')}
                    value={currentAddress?.streetName2}
                    onChange={(_, v) =>
                        setCurrentAddress(
                            new GetTaxpayerAddressDto({
                                ...currentAddress,
                                streetName2: v ?? '',
                            })
                        )
                    }
                />
            </div>
            <div className="bodyLine merge2">
                <TextField
                    label={t('form.TaxPayer.AdditionalInformation')}
                    value={currentAddress?.additionalInformation}
                    onChange={(_, v) =>
                        setCurrentAddress(
                            new GetTaxpayerAddressDto({
                                ...currentAddress,
                                additionalInformation: v ?? '',
                            })
                        )
                    }
                />
            </div>
            <div className="bodyLine">
                <TextField
                    label={t('form.TaxPayer.PostalCode')}
                    value={currentAddress?.postalCode}
                    onChange={(_, v) =>
                        setCurrentAddress(
                            new GetTaxpayerAddressDto({
                                ...currentAddress,
                                postalCode: v ?? '',
                            })
                        )
                    }
                />
            </div>
        </Panel>
    );
};

export default connector(EditAddress);